import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel} from '@mui/material';
import {obsStyles} from '../obsStyle';

function ObsRespiratoryDistress(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [distress, setDistress] = React.useState(allowDefaultValue ? props.distress : null);
    const [score, setScore] = React.useState(props.score || 0);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const distresses = [
        {
            id: 'none',
            description: t('None'),
            score: 0,
            default: true,
        },
        {
            id: 'mild',
            description: t('Mild'),
            score: 1,
        },
        {
            id: 'moderate',
            description: t('Moderate'),
            score: 2,
        },
        {
            id: 'severe',
            description: t('Severe'),
            score: 4,
        },
    ];

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setDistress(props.distress);
        }
    }, []);

    function setDefaults() {
        let def = _.find(distresses, (item) => !!item.default);
        if (def) setDistress(def);
    }

    useEffect(() => {
        if (!distress && allowDefaultValue) {
            if (!props.distress) {
                setDefaults();
            } else {
                setDistress(props.distress);
            }
        }
    }, [props.distress]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setDistress(null);
        } else {
            setDefaults();
        }
        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(distress) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [distress, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    distress,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, distress, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (distress) {
                //newScore = distress.score;
                newScore = getObsScore('respiratoryDistress', type, ageRangeYears, distress.id);
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, distress]);

    const onDistressChange = (value) => {
        setDistress(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Respiratory Distress')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onDistressChange(value)}
                                    options={distresses}
                                    value={distress}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(distress),
                        showStatusNR: type === obsTypes.pews,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsRespiratoryDistress.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    distress: PropTypes.object,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsRespiratoryDistress.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsRespiratoryDistress);